/**
 * Benachrichtigungs-Glocke (Topbar) + Drawer — Merge 2026-07-18 aus zwei Strängen:
 *  - main (#18): Aktivitäts-Drawer über GET /api/activity + datengetriebener Unseen-Punkt
 *    (localStorage merkt den letzten Besuch).
 *  - CX-Audit 2026-07-17: Sektion „Braucht Aufmerksamkeit" aus Live-Daten (überfällige/
 *    fehlgeschlagene Zahlungen, liegengebliebene Anfragen, ablaufende/angenommene Angebote) —
 *    JEDE Meldung navigiert zum Vorgang; dazu Event-Label im Feed (API-Join) und Klickziele
 *    auch für Einträge ohne eventId (inquiry.created -> /anfragen). Footer verlinkt die
 *    Benachrichtigungs-Einstellungen.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import type { EventRow, Inquiry, Payment } from "./types";
import { money } from "./format";
import { Drawer, Icon } from "./components";

interface ActivityEntry {
  id: string;
  eventId: string | null;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
  /** Merge: Anzeige-Label aus dem API-Join (leftJoin events) — null bei tenant-weiten Einträgen. */
  eventNumber?: string | null;
  eventOccasion?: string | null;
}

/** Zahlung inkl. Event-Kontext, wie sie GET /api/payments (aggregiert) liefert. */
interface PaymentWithEvent extends Payment {
  eventLabel: string;
  eventNumber: string | null;
  customerId: string;
}

/** Eine „Braucht Aufmerksamkeit"-Meldung: immer mit Klickziel — keine tote Anzeige. */
interface Notice {
  key: string;
  icon: string;
  title: string;
  sub: string;
  to: string;
  urgent: boolean;
  at: number;
}

const ACTION_META: Record<string, { label: string; icon: string }> = {
  "inquiry.created": { label: "Anfrage eingegangen", icon: "inbox" },
  "event.status_changed": { label: "Status geändert", icon: "sync_alt" },
  "event.cancelled": { label: "Veranstaltung storniert", icon: "cancel" },
  "offer.sent": { label: "Angebot gesendet", icon: "send" },
  "offer.resent": { label: "Angebot erneut gesendet", icon: "send" },
  "offer.chosen": { label: "Angebot angenommen", icon: "celebration" },
  "offer.restored": { label: "Version wiederhergestellt", icon: "history" },
  "cost_acceptance.requested": { label: "Kostenübernahme angefordert", icon: "draw" },
  "cost_acceptance.signed": { label: "Kostenübernahme signiert", icon: "verified" },
  "cost_acceptance.withdrawn": { label: "Kostenübernahme zurückgezogen", icon: "undo" },
  "invoice.created": { label: "Rechnung erstellt", icon: "receipt_long" },
  "invoice.sent": { label: "Rechnung gesendet", icon: "mail" },
  "invoice.cancelled": { label: "Rechnung storniert", icon: "undo" },
  "payment.recorded": { label: "Zahlung erfasst", icon: "payments" },
  "payment.refunded": { label: "Zahlung erstattet", icon: "payments" },
  "customer.anonymized": { label: "Kunde anonymisiert", icon: "person_off" },
};
const ACTOR_LABEL: Record<ActivityEntry["actorType"], string> = { user: "Team", customer: "Kunde", system: "System" };
const LS_KEY = "maestro.activity.lastSeen";

function meta(action: string): { label: string; icon: string } {
  return ACTION_META[action] ?? { label: action, icon: "bolt" };
}
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 7) return `vor ${d} Tg.`;
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** CX-Audit: Klickziel je Feed-Eintrag — Event-Detail, sonst der passende Bereich (nie tot,
 *  außer es gibt schlicht kein sinnvolles Ziel, z. B. „Kunde anonymisiert").
 *  CX-Fix 2026-07-18: inquiry.created trägt details.inquiry_id -> Deep-Link ?open= öffnet
 *  den Drawer der KONKRETEN Anfrage statt nur die Liste. */
function feedTarget(e: ActivityEntry): string | null {
  if (e.eventId) return `/veranstaltungen/${e.eventId}`;
  if (e.action === "inquiry.created") {
    const iid = e.details && typeof e.details["inquiry_id"] === "string" ? (e.details["inquiry_id"] as string) : null;
    return iid ? `/anfragen?open=${iid}` : "/anfragen";
  }
  if (e.action.startsWith("payment.")) return "/zahlungen";
  return null;
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Feed laden: beim Mount (fuer den Punkt) und immer wenn der Drawer offen ist (reload).
  const { data, reload } = useApi<ActivityEntry[]>("/api/activity?limit=40");
  const entries = useMemo(() => data ?? [], [data]);

  // „Braucht Aufmerksamkeit" — aus denselben tenant-lokalen Listen wie das Dashboard.
  const inquiriesQ = useApi<Inquiry[]>("/api/inquiries");
  const eventsQ = useApi<EventRow[]>("/api/events");
  const paymentsQ = useApi<PaymentWithEvent[]>("/api/payments");

  const notices = useMemo<Notice[]>(() => {
    const now = Date.now();
    const in3days = now + 3 * 86_400_000;
    const list: Notice[] = [];

    for (const p of paymentsQ.data ?? []) {
      if (!["pending", "processing"].includes(p.status) || !p.dueDate) continue;
      const due = new Date(p.dueDate).getTime();
      if (due >= now) continue;
      const days = Math.max(1, Math.round((now - due) / 86_400_000));
      list.push({
        key: `pay-${p.id}`,
        icon: "account_balance_wallet",
        title: `Zahlung überfällig — ${p.eventNumber ?? p.eventLabel}`,
        sub: `${money(p.amountCents)} · seit ${days} Tag${days === 1 ? "" : "en"} fällig`,
        to: `/veranstaltungen/${p.eventId}`,
        urgent: true,
        at: due,
      });
    }

    for (const e of eventsQ.data ?? []) {
      const label = e.occasion ?? e.number ?? "Veranstaltung";
      if (e.status === "payment_failed") {
        list.push({
          key: `fail-${e.id}`,
          icon: "error",
          title: `Zahlung fehlgeschlagen — ${label}`,
          sub: "Zahlungsweg im Vorgang klären",
          to: `/veranstaltungen/${e.id}`,
          urgent: true,
          at: new Date(e.createdAt).getTime(),
        });
      }
      if (e.status === "offer_sent" && e.offerValidUntil) {
        const until = new Date(e.offerValidUntil).getTime();
        if (until <= in3days) {
          list.push({
            key: `exp-${e.id}`,
            icon: "hourglass_bottom",
            title: `Angebot läuft ab — ${label}`,
            sub: until < now ? "Gültigkeit bereits verstrichen" : "Gültig bis in Kürze — nachfassen",
            to: `/veranstaltungen/${e.id}`,
            urgent: true,
            at: until,
          });
        }
      }
      if (e.status === "offer_chosen") {
        list.push({
          key: `won-${e.id}`,
          icon: "celebration",
          title: `Angebot angenommen — ${label}`,
          sub: "Rechnung/Zahlung einrichten",
          to: `/veranstaltungen/${e.id}`,
          urgent: false,
          at: new Date(e.createdAt).getTime(),
        });
      }
    }

    for (const i of inquiriesQ.data ?? []) {
      if (i.status !== "new") continue;
      const age = now - new Date(i.createdAt).getTime();
      const stale = age > 48 * 3_600_000;
      list.push({
        key: `inq-${i.id}`,
        icon: "inbox",
        title: `Neue Anfrage — ${i.customerName}`,
        sub: [i.guests ? `${i.guests} Gäste` : null, stale ? `wartet seit ${Math.round(age / 3_600_000)} h` : "Angebot erstellen"]
          .filter(Boolean)
          .join(" · "),
        // CX-Fix 2026-07-18: direkt in den Drawer DIESER Anfrage (?open=), nicht nur zur Liste.
        to: `/anfragen?open=${i.id}`,
        urgent: stale,
        at: new Date(i.createdAt).getTime(),
      });
    }

    return list.sort((a, b) => (a.urgent === b.urgent ? b.at - a.at : a.urgent ? -1 : 1)).slice(0, 8);
  }, [inquiriesQ.data, eventsQ.data, paymentsQ.data]);

  const urgentCount = notices.filter((n) => n.urgent).length;

  const [lastSeen, setLastSeen] = useState<string>(() => {
    try {
      return localStorage.getItem(LS_KEY) ?? "";
    } catch {
      return "";
    }
  });

  const newest = entries[0]?.createdAt ?? "";
  const hasUnseen = newest !== "" && newest > lastSeen;
  const unseenCount = lastSeen === "" ? entries.length : entries.filter((e) => e.createdAt > lastSeen).length;

  useEffect(() => {
    if (open) {
      reload();
      inquiriesQ.reload();
      eventsQ.reload();
      paymentsQ.reload();
      if (newest) {
        try {
          localStorage.setItem(LS_KEY, newest);
        } catch {
          /* localStorage evtl. blockiert - Punkt bleibt, kein harter Fehler */
        }
        setLastSeen(newest);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  return (
    <>
      <button
        type="button"
        className="relative rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10"
        title="Benachrichtigungen"
        aria-label={
          urgentCount > 0
            ? `Benachrichtigungen (${urgentCount} dringend)`
            : hasUnseen
              ? `Benachrichtigungen (${unseenCount} neu)`
              : "Benachrichtigungen"
        }
        onClick={() => setOpen(true)}
      >
        <Icon name="notifications" />
        {urgentCount > 0 ? (
          <span className="absolute right-0.5 top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-[#B23B32] px-1 text-[9.5px] font-bold text-white">
            {urgentCount > 9 ? "9+" : urgentCount}
          </span>
        ) : hasUnseen ? (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#C93E32] ring-2 ring-[#FFFDF8] dark:ring-night-bar" />
        ) : null}
      </button>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Benachrichtigungen"
        subtitle={
          notices.length > 0 || entries.length > 0
            ? [
                notices.length > 0 ? `${notices.length} offene${notices.length === 1 ? "r" : ""} Punkt${notices.length === 1 ? "" : "e"}` : null,
                entries.length > 0 ? `letzte ${entries.length} Aktivitäten` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
        footer={
          <button type="button" className="btn-secondary" onClick={() => go("/mein-konto?tab=benachrichtigungen")}>
            <Icon name="settings" className="text-[16px]" /> Einstellungen für Benachrichtigungen
          </button>
        }
      >
        {/* Sektion 1: Braucht Aufmerksamkeit (CX-Audit) — jede Meldung klickbar. */}
        {notices.length > 0 && (
          <div className="mb-5">
            <p className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/70">
              Braucht Aufmerksamkeit
            </p>
            <ul className="space-y-1">
              {notices.map((n) => (
                <li key={n.key}>
                  <button
                    type="button"
                    onClick={() => go(n.to)}
                    className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-surface-container-high dark:hover:bg-white/5"
                  >
                    <span
                      className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                        n.urgent
                          ? "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]"
                          : "bg-primary/15 text-primary"
                      }`}
                    >
                      <Icon name={n.icon} className="text-[17px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                        {n.title}
                      </span>
                      <span className="block truncate font-body-sm text-[12px] text-on-surface-variant/80">{n.sub}</span>
                    </span>
                    <Icon name="chevron_right" className="mt-1 shrink-0 text-[16px] text-on-surface-variant/50" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sektion 2: Aktivitäts-Feed (main #18), mit Event-Label + Klickzielen. */}
        {notices.length > 0 && entries.length > 0 && (
          <p className="mb-2 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/70">
            Zuletzt passiert
          </p>
        )}
        {entries.length === 0 && notices.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Icon name="notifications_off" className="text-3xl text-on-surface-variant/60" />
            <p className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Aktivitäten.</p>
          </div>
        ) : (
          <ul className="space-y-1">
            {entries.map((e) => {
              const m = meta(e.action);
              const unseen = lastSeen === "" || e.createdAt > lastSeen;
              const target = feedTarget(e);
              const eventLabel = e.eventOccasion ?? e.eventNumber ?? null;
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    disabled={!target}
                    onClick={() => {
                      if (target) go(target);
                    }}
                    className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      target ? "hover:bg-surface-container-high dark:hover:bg-white/5" : "cursor-default"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${unseen ? "bg-primary/15 text-primary" : "bg-surface-container-high text-on-surface-variant dark:bg-white/5"}`}>
                      <Icon name={m.icon} className="text-[17px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                          {m.label}
                          {eventLabel ? ` — ${eventLabel}` : ""}
                        </span>
                        {unseen && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                      </span>
                      <span className="block font-body-sm text-[12px] text-on-surface-variant/80">
                        {ACTOR_LABEL[e.actorType]}
                        {e.actorLabel ? ` · ${e.actorLabel}` : ""} · {timeAgo(e.createdAt)}
                      </span>
                    </span>
                    {target && <Icon name="chevron_right" className="mt-1 shrink-0 text-[16px] text-on-surface-variant/50" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Drawer>
    </>
  );
}
